import { createApi } from "@reduxjs/toolkit/query/react";
import {
  baseQuery,
  CREATE_TRADE_DATA,
  DELETE_TRADE_DATA,
  GET_ALL_TRADEDATA_LOGS,
  GET_ALL_TRADEDATA_REAL,
  GET_TARIFF_ANALYSIS_FINDINGS,
  START_TARIFF_ANALYSIS_BUILD,
  CLEAR_TARIFF_RATE_CACHE,
  GET_TRADE_DATA_COLUMNS,
} from "@/graphql";
interface TradeDataLogsReponse {
  getTasksByTenant: TradeDataLogsTypes;
}
interface PostTradeDataResponse {
  createTradeData: {
    data: string;
  };
}

export const tradeDataApi = createApi({
  reducerPath: "api/tradeData",
  tagTypes: ["tradeData", "tradeDataLogs"],
  baseQuery,
  endpoints: (builder) => ({
    getAllTradeData: builder.query<
      TradeDataResponse,
      {
        pageNo: number;
        pageSize: number;
        sortBy?: string;
        sortType?: number;
        clientId?: string;
        searchKeyword?: string;
        tenantId?: string;
        filters?: Record<string, any>;
        dateFrom?: string;
        dateTo?: string;
      }
    >({
      query: (variables) => ({
        document: GET_ALL_TRADEDATA_REAL,
        variables: { ...variables },
      }),
      providesTags: ["tradeData"],
      transformResponse: (response: TradeDataResponse) => response,
    }),
    getTradeDataColumns: builder.query<
      { data: string[] },
      {
        clientId?: string;
        tenantId?: string;
      }
    >({
      query: (variables) => ({
        document: GET_TRADE_DATA_COLUMNS,
        variables: { ...variables },
      }),
      transformResponse: (response: { getTradeDataColumns: { data: string[] } }) => 
        response.getTradeDataColumns,
    }),
    getTariffAnalysisFindings: builder.query<
      { tariffAnalysisFindings: { data: any[]; meta: { countDocs: number; pageNo: number; pageSize: number } } },
      {
        input: {
          pageNo: number;
          pageSize: number;
          sortBy?: string;
          sortType?: number;
          clientId?: string;
          tenantId?: string;
          searchKeyword?: string;
          filters?: Record<string, any>;
          dateFrom?: string;
          dateTo?: string;
        };
      }
    >({
      query: (variables) => ({
        document: GET_TARIFF_ANALYSIS_FINDINGS,
        variables,
      }),
      providesTags: ["tradeData"],
      transformResponse: (response: any) => response,
    }),
    startTariffAnalysisBuild: builder.mutation<
      { startTariffAnalysisBuild: { taskId: string; uploadTime: string; progressBar: boolean } },
      {
        input: {
          pageNo?: number;
          pageSize?: number;
          sortBy?: string;
          sortType?: number;
          clientId?: string;
          tenantId?: string;
          searchKeyword?: string;
          filters?: Record<string, any>;
          dateFrom?: string;
          dateTo?: string;
        };
      }
    >({
      query: (variables) => ({
        document: START_TARIFF_ANALYSIS_BUILD,
        variables,
      }),
      invalidatesTags: ["tradeData"],
      transformResponse: (response: any) => response,
    }),
    clearTariffRateCache: builder.mutation<
      { clearTariffRateCache: boolean },
      {
        tenantId?: string;
        clientId?: string;
        triggerRebuild?: boolean;
      }
    >({
      query: (variables) => ({
        document: CLEAR_TARIFF_RATE_CACHE,
        variables,
      }),
      invalidatesTags: ["tradeData"],
      transformResponse: (response: any) => response,
    }),
    postTradeData: builder.mutation<
      PostTradeDataResponse["createTradeData"],
      CreateTradeDataDTO
    >({
      query: ({ tradeData }) => ({
        document: CREATE_TRADE_DATA,
        variables: { tradeData },
      }),
      invalidatesTags: ["tradeData"],
      transformResponse: (response: PostTradeDataResponse) =>
        response.createTradeData,
    }),
    getAllTradeDataLogs: builder.query<
      TradeDataLogsReponse["getTasksByTenant"],
      {
        input: {
          clientId: string;
          uploadTime: string;
        };
      }
    >({
      query: (variables) => {
        return {
          document: GET_ALL_TRADEDATA_LOGS,
          variables: { ...variables },
        };
      },
      providesTags: ["tradeDataLogs"],
      transformResponse: (response: TradeDataLogsReponse) => {
        return response.getTasksByTenant;
      },
    }),
    deleteTradeData: builder.mutation<
      DeleteTradeDataResponse["deleteAllTradeData"],
      DeleteTradeDataVariables
    >({
      query: ({ tenantId, clientId }) => ({
        document: DELETE_TRADE_DATA,
        variables: { tenantId, clientId },
      }),
      invalidatesTags: ["tradeData"],
      transformResponse: (response: DeleteTradeDataResponse) => response.deleteAllTradeData,
    }),
  }),
});

export const {
  useGetAllTradeDataQuery,
  useLazyGetAllTradeDataQuery,
  useGetTradeDataColumnsQuery,
  useGetTariffAnalysisFindingsQuery,
  useLazyGetTariffAnalysisFindingsQuery,
  useStartTariffAnalysisBuildMutation,
  useClearTariffRateCacheMutation,
  usePostTradeDataMutation,
  useDeleteTradeDataMutation,
  // Logs api
  useGetAllTradeDataLogsQuery,
  useLazyGetAllTradeDataLogsQuery,
} = tradeDataApi;
